import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AlertBox from "./alertBox";
import MainButton from "./mainButton";
import { User } from "../type";
import { FaPenToSquare } from "react-icons/fa6";
import { FiMenu, FiX } from "react-icons/fi";

interface WindowProps {
  active: number;
  children?: React.ReactNode;
}

const SideMenu: React.FC<WindowProps> = ({ active, children }) => {
  const [signout, setSignout] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [userData, setUserData] = useState<User | null>(null);
  const navigate = useNavigate();

  const navItems = [
    { label: "Overall Dashboard", path: "/dashboard", index: 0 },
    { label: "Historical Data", path: "/history", index: 1 },
    { label: "Custom Trend", path: "/custom", index: 2 },
    { label: "Administration", path: "/administration", index: 3 },
  ];

  useEffect(() => {
    const loadUser = () => {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUserData(parsedUser);
        } catch (err) {
          console.error("Invalid user data:", err);
          localStorage.removeItem("user");
          navigate("/login");
        }
      }
    };

    loadUser(); // เรียกครั้งแรก

    // 🔥 listen event "user-updated"
    const handler = () => loadUser();
    window.addEventListener("user-updated", handler);

    return () => {
      window.removeEventListener("user-updated", handler);
    };
  }, [navigate]);

  const pushNavigate = (path: string) => {
    navigate(path);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    navigate("/login");
  };

  if (!userData) return null;

  const renderMenuItems = () => (
    <>
      {navItems.map(({ label, path, index }) => (
        <div
          key={index}
          className={`${
            active === index && !signout
              ? "text-white font-semibold bg-gradient-to-r from-green-normal to-green hover:from-green hover:to-green-normal"
              : "text-gray-nor"
          } text-sm py-6 cursor-pointer px-8 hover:text-white`}
          onClick={() => pushNavigate(path)}
        >
          {label}
        </div>
      ))}

      <div
        className={`${
          signout ? "text-white font-semibold" : "text-gray-nor"
        } text-sm py-6 cursor-pointer px-8 hover:text-white`}
        onClick={() => setSignout(true)}
      >
        Logout
      </div>
    </>
  );

  const renderUserSection = () => (
    <div className="flex justify-between gap-4 bg-gray-dark w-full px-4 py-8 lg:py-6 items-center text-white">
      <div className="flex gap-4">
        <img
          src={userData.imageUrl ?? "/default.png"}
          alt="default"
          className="w-12 h-12 rounded-full"
        />
        <div className="flex flex-col cursor-default">
          <p className="font-bold">
            {userData.firstName} {userData.lastName}
          </p>
          <p className="font-light">{userData.role.split("_").join(" ")}</p>
        </div>
      </div>

      <FaPenToSquare
        className="text-2xl mr-4 cursor-pointer"
        onClick={() => pushNavigate("/profile")}
      />
    </div>
  );

  return (
    <div className="flex h-screen font-anuphan bg-slate-50">
      {/* ===== Desktop Sidebar (md ขึ้นไป) ===== */}
      <aside className="hidden xl:flex xl:flex-col md:w-80 bg-navy py-8 relative">
        {/* Logo + Title */}
        <div className="text-white text-2xl flex items-center gap-3 px-8">
          <img src="/logo.png" alt="logo" className="w-20" />
          <div>
            <p className="text-xl font-bold text-left">
              <b>Algae Deep Tech</b>
            </p>
            <p className="text-lg font-bold text-left">Dashboard</p>
          </div>
        </div>

        {/* Menu */}
        <div className="grid grid-cols-1 mt-8">
          {renderMenuItems()}
        </div>

        {/* User */}
        <div className="absolute bottom-0 w-full">{renderUserSection()}</div>
      </aside>

      {/* ===== Mobile Navbar + Drawer (ต่ำกว่า md) ===== */}
      {/* Top bar */}
      <div className="xl:hidden fixed top-0 left-0 right-0 z-20 bg-navy text-white flex items-center justify-between p-4">
        <button
          type="button"
          className="flex items-center justify-center"
          onClick={() => setIsOpen(true)}
        >
          <FiMenu className="text-2xl" />
        </button>
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="logo" className="w-10" />
          <div className="leading-tight">
            <p className="text-sm font-bold">
              <b>Algae Deep Tech</b>
            </p>
            <p className="text-xs font-semibold">Dashboard</p>
          </div>
        </div>
        <div className="w-6" /> {/* ช่องว่าง balance icon ฝั่งซ้าย */}
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 xl:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed inset-y-0 left-0 z-30 w-96 bg-navy pt-8 transform transition-transform duration-200 xl:hidden ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* ทำ drawer ให้เป็น flex column เต็มสูง */}
        <div className="flex flex-col h-full">
          {/* Header + close */}
          <div className="flex items-center justify-between px-6">
            <div className="flex items-center gap-3 text-white">
              <img src="/logo.png" alt="logo" className="w-14" />
              <div>
                <p className="text-base font-bold">
                  <b>Algae Deep Tech</b>
                </p>
                <p className="text-sm font-bold">Dashboard</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-white"
            >
              <FiX className="text-2xl" />
            </button>
          </div>

          {/* Menu - ให้ขยายเต็มพื้นที่ที่เหลือ */}
          <div className="flex-1 overflow-y-auto mt-6">{renderMenuItems()}</div>

          {/* User section - จะอยู่ล่างโดยอัตโนมัติ */}
          <div className="shrink-0">{renderUserSection()}</div>
        </div>
      </div>

      {/* ===== Main Content ===== */}
      <div className="flex-1 flex">
        <main className="flex-1 xl:overflow-y-auto py-4 px-1 mt-12 xl:mt-0 md:p-8">{children}</main>
      </div>

      {/* Alert logout */}
      <AlertBox open={signout}>
        <div className="text-lg text-center">Logout ?</div>
        <div className="flex mt-5 justify-between">
          <div className="w-24 text-sm cursor-pointer">
            <MainButton
              label="Cancel"
              color="bg-navy"
              onClickHandler={() => setSignout(false)}
            />
          </div>
          <div className="w-24 text-sm cursor-pointer ml-5">
            <MainButton
              label="Ok"
              color="bg-green"
              onClickHandler={handleLogout}
            />
          </div>
        </div>
      </AlertBox>
    </div>
  );
};

export default SideMenu;
